export type ReminderType = 'meeting' | 'task' | 'event' | 'deadline' | 'alert';

export type ReminderPriority = 'low' | 'medium' | 'high';

export type ReminderStatus = 'pending' | 'snoozed' | 'completed' | 'cancelled';

export interface ReminderUser {
  id: number;
  name: string;
  email: string;
  is_read: boolean;
  read_at?: string;
  user_completed: boolean;
  user_completed_at?: string;
}

export interface ReminderCreator {
  id: number;
  name: string;
  email: string;
}

export interface ReminderModule {
  type: string;  // 'Bid', 'Job', 'Task', etc.
  id: number;
  name: string;
  customer_id?: number;    // Present for Proposal reminderables
  customer_name?: string;  // Present for Proposal reminderables
  bid_id?: number;         // Present for Proposal reminderables
  bid_name?: string;       // Present for Proposal reminderables
}

export interface Reminder {
  id: string | number;
  title: string;
  description?: string;
  reminder_date: string;  // Backend uses reminder_date
  reminder_time: string;  // Backend uses reminder_time
  timezone?: string;
  alert_before_minutes?: number; // Minutes before reminder time to fire the alert (0 = at time)
  location?: string;
  type: ReminderType;
  priority: ReminderPriority;
  status: ReminderStatus;
  is_private: boolean;

  // Computed attributes from backend
  is_overdue: boolean;
  due_in: string;

  // Status timestamps
  completed_at?: string;
  snoozed_until?: string;
  snooze_count?: number;

  // Relationships
  created_by?: ReminderCreator;
  completed_by?: ReminderCreator;
  assigned_users?: ReminderUser[];
  reminderable?: ReminderModule;

  // Timestamps
  created_at: string;
  updated_at: string;

  // Legacy fields for backward compatibility
  date?: string;  // Alias for reminder_date
  time?: string;  // Alias for reminder_time
  completed?: boolean;  // Alias for status === 'completed'
  overdue?: boolean;  // Alias for is_overdue
  dueIn?: string;  // Alias for due_in
  createdAt?: string;  // Alias for created_at
  updatedAt?: string;  // Alias for updated_at
  snoozedUntil?: string;  // Alias for snoozed_until
}

export interface ReminderFormData {
  title: string;
  description?: string;
  reminder_date: string;
  reminder_time: string;
  timezone?: string;
  alert_before_minutes?: number;
  type: ReminderType;
  priority: ReminderPriority;
  location?: string;
  is_private?: boolean;
  user_ids?: number[];  // For shared reminders
  reminderable_type?: string;  // For module linking
  reminderable_id?: number;  // For module linking
}

export interface ReminderLeadOption {
  value: number;  // minutes before the reminder time
  label: string;
}

// Shared "remind me before" options used by every reminder-creation surface
export const REMINDER_LEAD_TIME_OPTIONS: ReminderLeadOption[] = [
  { value: 0, label: 'At time of reminder' },
  { value: 5, label: '5 minutes before' },
  { value: 10, label: '10 minutes before' },
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 1440, label: '1 day before' },
];

export interface ReminderFilter {
  type?: ReminderType | 'all';
  priority?: ReminderPriority | 'all';
  status?: 'all' | 'pending' | 'completed' | 'overdue' | 'snoozed';
  searchTerm?: string;
  dateRange?: {
    start: string;
    end: string;
  };
}

export interface ReminderStats {
  overdueCount: number;
  todayCount: number;
  laterCount: number;
  totalCount: number;
  completedCount: number;
}

// API Response interfaces
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
  };
}

export interface ApiError {
  success: false;
  message: string;
  errors?: { [key: string]: string[] };
}
